import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Lottie from 'react-lottie';
import chat from 'static/animations/chat.json';

import newIconAnimation from 'static/animations/new.json';

import { CloseButton, Button } from 'components';

import { UpArrow } from 'static';
import {
  StyledBody,
  StyledHeader,
  StyledPopup,
  StyledFooter,
} from '../importPopup/styled';
import { Wrapper } from '../styled';
import { breakpoints } from 'theme';
import { useMedia } from 'react-use';
import { ModalTypes } from 'types/modal';
import { openModal } from 'store/actionCreators/modal';
import { useDispatch } from 'react-redux';

type Props = {
  onClose: () => void;
  isOpen: boolean;
};

const ChatFeaturePopup: React.FC<Props> = ({ onClose, isOpen }) => {
  const dispatch = useDispatch();
  const isMobileView = useMedia(breakpoints.downSmPlus);
  const { i18n } = useTranslation();
  const isGerman = i18n.language === 'de';

  const handleClick = () => {
    onClose();

    setTimeout(() => {
      dispatch(
        openModal({
          type: ModalTypes.CHAT_POPUP,
        }),
      );
    }, 1000);
  };

  const buttonStyle = {
    textTransform: 'none',
    fontSize: '16px',
  };

  const animationOptions = {
    animationData: newIconAnimation,
    loop: true,
    autoplay: true,
  };

  return (
    <>
      {createPortal(
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment*/
        /* @ts-ignore*/
        <AnimatePresence>
          {isOpen && (
            <Wrapper
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <StyledPopup>
                <CloseButton onClick={onClose} />
                <PerfectScrollbar
                  options={{
                    wheelSpeed: 0.1,
                    wheelPropagation: false,
                    minScrollbarLength: 4,
                  }}
                >
                  <StyledHeader>
                    <div
                      style={{
                        position: 'relative',
                        bottom: '0px',
                        backgroundColor: 'white',
                      }}
                    >
                      <Lottie
                        options={{
                          animationData: chat,
                          loop: true,
                          autoPlay: true,
                        }}
                        width={isMobileView ? '180px' : '40vh'}
                        height={isMobileView ? '180px' : '40vh'}
                      />
                    </div>
                  </StyledHeader>
                  <StyledBody>
                    <h3>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          justifyContent: 'center',
                        }}
                      >
                        <Lottie
                          options={animationOptions}
                          width="48px"
                          height="48px"
                          style={{ margin: '0px' }}
                        />
                      </span>
                      <span>
                        {isGerman ? 'Chat ist da! ' : 'Chat is here! '}
                      </span>
                    </h3>
                    <p style={{ textAlign: 'center', lineHeight: 1.3 }}>
                      {isGerman
                        ? 'Ab sofort kannst du direkt mit Mitgliedern chatten – 1:1 oder in Gruppen.'
                        : 'You can now message members directly – 1:1 or in group chats.'}
                    </p>
                    <p style={{ textAlign: 'center', lineHeight: 1.3 }}>
                      {isGerman
                        ? 'Planung & Kommunikation – alles in Gumb.'
                        : 'Planning & communication – all in one place.'}
                    </p>
                    <p
                      style={{
                        textAlign: 'center',
                        lineHeight: 1.5,
                        marginTop: '14px',
                        fontSize: '16px',
                      }}
                    >
                      {isGerman ? (
                        <>
                          <strong>Web:</strong> Jetzt sichtbar
                        </>
                      ) : (
                        <>
                          <strong>Web:</strong> Available now
                        </>
                      )}
                    </p>
                    <p
                      style={{
                        textAlign: 'center',
                        lineHeight: 1.5,
                        fontSize: '16px',
                      }}
                    >
                      {isGerman ? (
                        <>
                          <strong>Mobile:</strong> Nach App-Update
                        </>
                      ) : (
                        <>
                          <strong>Mobile:</strong> After installing the latest
                          app version
                        </>
                      )}
                    </p>
                    <p
                      style={{
                        textAlign: 'center',
                        lineHeight: 1.5,
                        fontSize: '16px',
                      }}
                    >
                      {isGerman ? (
                        <>
                          <strong>Hinweis:</strong> Apple & Google
                          veröffentlichen nicht gleichzeitig.
                        </>
                      ) : (
                        <>
                          <strong>Note:</strong> Apple & Google release at
                          different times.
                        </>
                      )}
                    </p>
                  </StyledBody>
                  <StyledFooter>
                    <Button
                      variant="secondary"
                      onClick={onClose}
                      buttonStyles={buttonStyle}
                      {...{ rounded: true }}
                    >
                      {isGerman ? 'Später' : 'Later'}
                    </Button>
                    <Button
                      onClick={handleClick}
                      buttonStyles={{ ...buttonStyle, color: 'black' }}
                      {...{ rounded: true }}
                    >
                      {isGerman ? 'Jetzt ansehen' : 'Check it out'}
                    </Button>
                  </StyledFooter>
                </PerfectScrollbar>
              </StyledPopup>
            </Wrapper>
          )}
        </AnimatePresence>,
        document.getElementById('modal'),
      )}
    </>
  );
};

export default ChatFeaturePopup;
